import { HttpClient } from "@angular/common/http";
import { ApplicationRef, ComponentFactoryResolver, EmbeddedViewRef, Injectable, Injector, Optional } from "@angular/core";
import { BiCountryId } from "@globals/enums/BiLanguageAndCountryId";
import { ColorEnum } from "@globals/enums/ColorEnum";
// Import leaflet here so that all extensions modify the same, global leaflet module variable
import { BiTranslateService } from "@globals/bi-translate";
import { isStringNullOrEmpty } from "@globals/helper-functions";
import { AddressModel } from "@globals/openapi-models/model/addressModel";
import { CustomerMapTemplateDTO } from "@globals/openapi-models/model/customerMapTemplateDTO";
import { EntryAddressSelectionDto } from "@globals/openapi-models/model/entryAddressSelectionDto";
import { GeoObject } from "@globals/openapi-models/model/geoObject";
import { LatLngObject } from "@globals/openapi-models/model/latLngObject";
import { MapLayerModel } from "@globals/openapi-models/model/mapLayerModel";
import { MapLayerType } from "@globals/openapi-models/model/mapLayerType";
import * as L from "leaflet";
import "leaflet-draw";
import drawLocales from "leaflet-draw-locales";
import "leaflet.markercluster";
import "leaflet.vectorgrid/dist/Leaflet.VectorGrid.js";
import { map, Observable, of, map as rxjsMap, switchMap, take, tap } from "rxjs";
import { BiMapArea } from "./BiMapArea";
import { BiMapComponentAddressModel } from "./BiMapComponentAddressModel";
import { BiMapMarker } from "./BiMapMarker";
import { BiArchiveMessagesMapToggleComponent } from "./bi-archive-messages-map-toggle.component.ts/bi-archive-messages-map-toggle.component";
import { BiLeafletGoogleSearchComponent } from "./bi-leaflet-googlesearch/bi-leaflet-googlesearch.component";
import { IMapLegendItem } from "./bi-map-legend/IMapLegendItem";
import { BiMapLegendComponent } from "./bi-map-legend/bi-map-legend.component";
import { LantmäterietMapSetupService } from "./map-utility-services/lantmäteriet-map-setup.service";

@Injectable()
export class BiMapService {
  private baseApiUri = "/api/map/";
  private currentMapLayer: L.Layer;
  private _currentMapLayerId: number;

  /**
   * Refers to the currently active MapLayer (base layer from backend). Not to be confiused with a Leaflet Layer id as all shapes are Layers and have an Id!
   **/
  public get currentMapLayerId() {
    return this._currentMapLayerId;
  }

  private layersControl: L.Control.Layers;

  /**
   * All the available map layers
   */
  private _mapLayers: {
    [name: string]: { layer: L.Layer; id: number };
  };
  public get mapLayers() {
    return this._mapLayers;
  }
  public setMapLayers(layers: any) {
    this._mapLayers = layers;
  }

  /**
   * List of the currently found addresses from leaflet search result
   */
  private currentSearchedAddresses: Array<AddressModel>;

  constructor(
    private http: HttpClient,
    private translator: BiTranslateService,
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
    @Optional() private lantmäterietSetupService: LantmäterietMapSetupService
  ) {}

  public getGeneralMapSettings(): Observable<L.MapOptions> {
    // As we use the translator for getting translations here, we must make sure that the translations have been loaded before proceeding
    const obs$: Observable<any> = Object.keys(this.translator.translations).length > 0 ? of(undefined) : this.translator.onLangChange;

    return obs$.pipe(
      map(() => {
        return {
          zoom: 7,
          zoomSnap: 0,
          zoomDelta: 0.4,
          wheelPxPerZoomLevel: 100,
          minZoom: 4,
          maxZoom: 18,
          center: new L.LatLng(56.176361, 9.554922),
          scrollWheelZoom: true,
          touchZoom: true
        };
      })
    );
  }

  /**
   * Calls server for getting addresses withing the areas represented by the passed geoobjects. These geo objects can represent makers
   * as well as polygons.
   */
  public getAddressesFromDraw(geoObjects: GeoObject[], onlyProperties: boolean) {
    return this.http.post<EntryAddressSelectionDto[]>(this.baseApiUri + "GetAddressFromShapes", {
      geoObjects,
      onlyProperties
    });
  }

  /**
   * Returns the local, cached address search result. E.i. does NOT make an HTTP call
   */
  public getSearchedAddresses = () => this.currentSearchedAddresses;

  /**
   * Searches for an address in the local, cached address search result. E.i. does NOT make an HTTP call.
   */
  public getSearchedAddress = (latLng: L.LatLng) => this.currentSearchedAddresses.find(a => a.latitude === latLng.lat && a.longitude === latLng.lng);

  //#region Map templates
  public getCustomerMapTemplates(customerId: number) {
    return this.http.get<Array<CustomerMapTemplateDTO>>(this.baseApiUri + "GetCustomerMapTemplates", { params: { customerId } });
  }

  public createCustomerMapTemplate(customerMapTemplate: CustomerMapTemplateDTO) {
    return this.http.post<number>(this.baseApiUri + "CreateCustomerMapTemplate", customerMapTemplate);
  }

  public updateCustomerMapTemplate(id: number, name: string) {
    return this.http.put<CustomerMapTemplateDTO>(this.baseApiUri + "UpdateCustomerMapTemplate", null, { params: { id, name } });
  }

  public deleteCustomerMapTemplate(templateId: number) {
    return this.http.delete(this.baseApiUri + "DeleteCustomerMapTemplate/" + templateId);
  }
  //#endregion

  // --
  /**
   * Adds a search input to the map so you can search for addresses using zip/city, street and house number
   * @param noPosListSearch Whether the address search shouldn't be limited by positive lists on server.
   */
  public setupSearchControl(map: L.Map, countryId?: BiCountryId) {
    // Create component and set it properties
    const searchComponentRef = this.resolver.resolveComponentFactory(BiLeafletGoogleSearchComponent).create(this.injector);
    this.appRef.attachView(searchComponentRef.hostView); // for making the component a part of the Angular's life cycle
    searchComponentRef.setInput("map", map);
    if (countryId) searchComponentRef.setInput("countryId", countryId);
    // Append the component to the top left leaflet controls container using some casting magic
    const leafletLeftSideContainer = $(".leaflet-top.leaflet-left");
    if (leafletLeftSideContainer) {
      leafletLeftSideContainer.append((searchComponentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement);
    }
  }

  /**
   * Dynamically creates and inserts the component for "Show WEB-messages from archive"-feature.
   * @param viewContainer Needed by Angular for creating components dynamically. Just pass any ViewContainerRef
   * @param nrOfDaysBackInArchive The number of days back in archive that user will be able to see
   * @param onToggledCallback Callback to run when the "Show "
   */
  public setupShowArchiveFeature(nrOfDaysBackInArchive: number, onToggledCallback: (showFromArchive: boolean) => void) {
    const showArchiveToggleComponent = this.resolver.resolveComponentFactory(BiArchiveMessagesMapToggleComponent).create(this.injector);
    this.appRef.attachView(showArchiveToggleComponent.hostView);
    showArchiveToggleComponent.instance.archiveDays = nrOfDaysBackInArchive;
    showArchiveToggleComponent.instance.toggleShowFromArchive.subscribe(showFromArchive => onToggledCallback(showFromArchive));
    // Append the component to the top right leaflet controls container using some casting magic
    const leafletRightSideContainer = $(".leaflet-top.leaflet-right");
    if (leafletRightSideContainer) {
      leafletRightSideContainer.append((showArchiveToggleComponent.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement);
    }
  }

  public setupReloadButton(callback: () => void) {
    const reloadButton = $(`
    <a type="button" role="button" tabindex="0" href="#" aria-label="reload" class="p-component pointer leaflet-control-zoom leaflet-bar leaflet-control leaflet-reload-button">
        <span class="fa-solid fa-sync-alt" aria-hidden="true"></span>
    </a>`).on("click", e => {
      e.preventDefault();
      callback();
    });

    const leafletLeftSideContainer = $(".leaflet-top.leaflet-left");
    leafletLeftSideContainer.append(reloadButton);
  }

  /**
   * Helper for setting up the map draw feature
   * @param deletePolygonsCallback Callback to fire when 1 or more polygons has been deleted. Passed a Leaflet LayerGroup containing the deleted layers (polygons)
   * @param changeCallback Callback to fire when any change has been made on the draw layer (draw, edit or delete)
   * @param drawLayer The layer to add all drawn shapes to.
   */
  public setupDrawControl(map: L.Map, drawLayer: L.FeatureGroup, deletePolygonsCallback: (polygons: L.LayerGroup) => void, options?: L.Control.DrawConstructorOptions) {
    // This Version Of leaflet Has a Bug When Editing a Circle!!
    // Update Leafet to get Rid of this..
    L.Edit.Circle = L.Edit.CircleMarker.extend({
      _createResizeMarker: function () {
        const center = this._shape.getLatLng(),
          resizemarkerPoint = this._getResizeMarkerPoint(center);

        this._resizeMarkers = [];
        this._resizeMarkers.push(this._createMarker(resizemarkerPoint, this.options.resizeIcon));
      },

      _getResizeMarkerPoint: function (latlng) {
        const delta = this._shape._radius * Math.cos(Math.PI / 4),
          point = this._map.project(latlng);
        return this._map.unproject([point.x + delta, point.y - delta]);
      },

      _resize: function (latlng) {
        const moveLatLng = this._moveMarker.getLatLng();
        let radius;

        if (L.GeometryUtil.isVersion07x()) {
          radius = moveLatLng.distanceTo(latlng);
        } else {
          radius = this._map.distance(moveLatLng, latlng);
        }

        // **** This fixes the cicle resizing ****
        this._shape.setRadius(radius);

        this._map.fire(L.Draw.Event.EDITRESIZE, { layer: this._shape });
      }
    });

    // First add translations
    this.setupDrawLocales();

    if (!options) {
      options = {
        position: "topright",
        draw: {
          polygon: { showLength: true },
          polyline: { showLength: true },
          rectangle: <any>{ showArea: false },
          circle: {
            metric: true,
            showRadius: true
          },
          circlemarker: false,
          marker: {
            icon: L.icon({
              iconUrl: "/StaticFiles/images/leaflet/marker-icon.png",
              shadowUrl: "/StaticFiles/images/leaflet/marker-shadow.png",
              iconAnchor: [15, 40],
              className: "bi-pin-marker"
            }),
            repeatMode: true
          }
        },
        edit: {
          featureGroup: drawLayer
        }
      };
    }

    new L.Control.Draw(options).addTo(map);
    drawLayer.addTo(map); // important! otherwise, drawn items aren't shown
    // When says "something has been drawn!", add the drawn layer to the feature group of drawn layers
    map.on(L.Draw.Event.CREATED, (e: L.DrawEvents.Created) => {
      const layer = e.layer;
      drawLayer.addLayer(layer);

      // Create ToolTip - ( Circles )
      if (e.layerType === "circle") {
        const circle = e as any,
          radius = circle.layer._mRadius;

        e.layer.bindTooltip(this.translator.instant("shared.Radius") + ": " + Math.floor(radius).toString() + " meter").openTooltip();
      } else if (e.layerType === "rectangle") {
        const rectangle = e as any;
        const coords = rectangle.layer.getLatLngs();
        e.layer
          .bindTooltip(
            this.translator.instant("shared.Width") +
              ": " +
              Math.floor(coords[0][1].distanceTo(coords[0][2])).toString() +
              " meter<br />" +
              this.translator.instant("shared.Height") +
              ": " +
              Math.floor(coords[0][0].distanceTo(coords[0][1])).toString() +
              " meter"
          )
          .openTooltip();
      }
    });

    // Update ToolTip - (Circles)
    map.on(L.Draw.Event.EDITRESIZE, (event: any) => {
      if (event.layer && event.layer._mRadius) {
        // Update Tool-Tip
        $(".leaflet-draw-tooltip-subtext").html(`${this.translator.instant("shared.Radius")}: ${Math.floor(event.layer.getRadius())} meter`);
        event.layer.bindTooltip(this.translator.instant("shared.Radius") + ": " + Math.floor(event.layer._mRadius).toString() + " meter").openTooltip();
      }
    });

    // When a polygon has been deleted, markers inside must be deleted also
    map.on(L.Draw.Event.DELETED, (e: L.DrawEvents.Deleted) => deletePolygonsCallback(e.layers));

    this.fixDoubleClickToFinishDrawing();
  }

  /**
   * Get the LatLng bounds and center coordinates of addresses. Returned as an object with the values of bounds and center.
   * @param  mapAddresses
   * @param  defaultLatitude The default value is 0.
   * @param  defaultlongitude The default value is 0.
   */
  public getMapBoundsAndCenterOfAddresses(mapAddresses: Array<AddressModel>, defaultLatitude = 0, defaultlongitude = 0) {
    let latitude = 0;
    let longitude = 0;

    mapAddresses.forEach(x => {
      latitude += x.latitude;
      longitude += x.longitude;
    });

    latitude = latitude / mapAddresses.length;
    longitude = longitude / mapAddresses.length;

    if (latitude == 0 && longitude == 0) {
      latitude = defaultLatitude;
      longitude = defaultlongitude;
    }

    return {
      center: <LatLngObject>{ lat: latitude, lng: longitude },
      bounds: new L.LatLngBounds(mapAddresses.map(a => [a.latitude, a.longitude]))
    };
  }

  /**
   * Sets up the layers control for switching between layers. First, we make an API call to get the available layers
   * for the country and then we create the layers control (and eventually remove the old one)
   * defaultMapType 2 = hybrid
   */
  public setupLayersControl(map: L.Map, onlyForDriftStatus = false, defaultMapLayerId: number | undefined, countryId: BiCountryId, profileId?: number, isSuperAdmin = false): Observable<any> {
    const params = {
      countryId,
      onlyForDriftStatus: onlyForDriftStatus
    };

    if (profileId) params["profileId"] = profileId;

    return this.http
      .get<MapLayerModel[]>(this.baseApiUri + "GetMapLayers", {
        params
      })
      .pipe(
        switchMap(layers => {
          // As the layer names are returned as translation keys, make sure that translations are loaded!
          if (Object.keys(this.translator.translations).length > 0) return of(layers);
          else {
            return this.translator.onLangChange.pipe(
              take(1),
              rxjsMap(() => layers)
            );
          }
        }),
        tap((layers: MapLayerModel[]) => {
          this._mapLayers = {};
          layers
            .filter(layer => layer.isBaseLayer)
            .forEach((layer, i) => {
              if (layer.nameTranslationKey) layer.name = this.translator.instant(layer.nameTranslationKey);

              const baseLayer = this.generateLeafletLayer(layer) as L.Layer;

              this._mapLayers[layer.name] = {
                layer: baseLayer,
                id: layer.id
              };

              if ((!defaultMapLayerId && i === 0) || layer.id === defaultMapLayerId) {
                this.currentMapLayer = baseLayer;
                this._currentMapLayerId = layer.id;
              }

              if (layer.layerUrl.indexOf("hereapi") !== -1) this.setupTileLayerErrorHandling(baseLayer as L.TileLayer);
            });

          // Disabled Lantmäteriet Basis in task #10977
          // if (!onlyForDriftStatus && countryId === BiCountryId.SE && customerId === 9) {
          //   const swedishBaseLayers = this.lantmäterietSetupService.getBaseLayer(1);
          //   this._mapLayers[swedishBaseLayers.name] = {
          //     layer: swedishBaseLayers.layer,
          //     id: swedishBaseLayers.id
          //   };
          // }

          // Add World Street Map as a static base layer
          // const worldStreetMap = esri.tiledMapLayer({
          //   url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer",
          //   attribution: "Esri, World Street Map"
          // });

          // this._mapLayers["World Street Map"] = {
          //   layer: worldStreetMap,
          //   id: 9999
          // };

          // // Tilføj ArcGIS Feature Layer
          // const arcgisFeatureLayer = esri.featureLayer({
          //   url: "https://sampleserver6.arcgisonline.com/arcgis/rest/services/Census/MapServer/3",
          //   style: {
          //     color: "#FF0000",
          //     weight: 2
          //   }
          // });

          const mapLayers: { [layerName: string]: L.Layer } = {};
          Object.keys(this._mapLayers).forEach(layerName => {
            mapLayers[layerName] = this._mapLayers[layerName].layer;
          });

          if (this.layersControl) this.layersControl.remove();

          this.layersControl = L.control.layers(mapLayers).addTo(map);
          this.currentMapLayer.addTo(map);

          if (isSuperAdmin && countryId === BiCountryId.SE) {
            const kommunerLayer = this.lantmäterietSetupService.loadKommunerLayer();
            this.layersControl.addOverlay(kommunerLayer, "Kommuner 2025");
          }

          map.on("baselayerchange", (e: L.LayersControlEvent) => {
            this.currentMapLayer = e.layer;
            this._currentMapLayerId = this._mapLayers[e.name].id;
          });

          layers
            .filter(layer => !layer.isBaseLayer)
            .forEach(layer => {
              this.layersControl.addOverlay(this.generateLeafletLayer(layer) as L.Layer, layer.nameTranslationKey ? this.translator.instant(layer.nameTranslationKey) : layer.name);
            });

          // Tilføj ArcGIS Feature Layer som overlay
          //this.layersControl.addOverlay(arcgisFeatureLayer, "Census Feature Layer");
        })
      );
  }

  private generateLeafletLayer(layer: MapLayerModel): L.Layer {
    const layerOptionsCommon: L.GridLayerOptions = {
      attribution: layer.attribution,
      updateWhenIdle: true,
      updateWhenZooming: false,
      updateInterval: 400
    };

    // ArcGIS REST Layer
    // if (layer.layerType === MapLayerType.Arcgisrest) {
    //   if (!L.esri || !L.esri.tiledMapLayer) {
    //     console.error("Esri Leaflet is not properly loaded!");
    //     return null;
    //   }

    //   return L.esri.tiledMapLayer({
    //     url: layer.layerUrl,
    //     attribution: layer.attribution || "Esri, World Street Map"
    //   });
    // }

    // TileLayer
    if ((layer.layerType === MapLayerType.Unknown && layer.isBaseLayer) || layer.layerType === MapLayerType.Tilelayer) {
      return new L.TileLayer(layer.layerUrl, layerOptionsCommon);
    }

    // WMS Layer
    if (layer.layerType === MapLayerType.Wms || layer.layerType === MapLayerType.Unknown) {
      return new L.TileLayer.WMS(layer.layerUrl, {
        layers: layer.layers,
        attribution: layer.attribution,
        crs: L.CRS.EPSG3857,
        ...(layer.properties ? JSON.parse(layer.properties) : {})
      });
    }

    // WMTS Layer
    if (layer.layerType === MapLayerType.Wmts) {
      let format = "png";
      if (layer.properties) {
        const props = JSON.parse(layer.properties);
        if (!isStringNullOrEmpty(props.format)) format = props.format;
      }
      return format === "mvt" ? (this.lantmäterietSetupService.getMvtLayer(layer.id) as L.Layer) : this.lantmäterietSetupService.getBaseLayer(layer.id).layer;
    }

    // Hvis ingen af typerne matcher, returnér null (eller håndtér fejl)
    console.error("Unsupported layer type:", layer.layerType);
    return null;
  }

  /**
   * Setup error handling fior a tile image request fails. Sometimes, we get an HTTP429 (too many requests) and therefore need to retry after a while.
   * If an error occurs, we retry the tile request up to 3 times.
   */
  private setupTileLayerErrorHandling(layer: L.TileLayer) {
    const failedCoordCounts: { [key: string]: number } = {};

    layer.on("tileerror", event => {
      const coordsString = `${event.coords.x}${event.coords.y}${event.coords.z}`;
      if (failedCoordCounts[coordsString] > 3) return delete failedCoordCounts[coordsString];
      else {
        if (failedCoordCounts[coordsString] != undefined) ++failedCoordCounts[coordsString];
        else failedCoordCounts[coordsString] = 1;
        setTimeout(() => (event.tile.src = `${layer.getTileUrl(event.coords)}&xx=${Math.random()}`), 1500);
      }
    });
    layer.on("tileunload", event => {
      const coordsString = `${event.coords.x}${event.coords.y}${event.coords.z}`;
      if (failedCoordCounts[coordsString] != undefined) delete failedCoordCounts[coordsString];
    });
  }

  public addSwedishMapOverlays(map: L.Map) {
    const defaultColor = this._currentMapLayerId === 2 ? "yellow" : "black";
    this.lantmäterietSetupService.setupByggnadVectorGridOverlay(defaultColor, this.layersControl);
    this.lantmäterietSetupService.setupFastighetsIndelningVectorGridOverlay(defaultColor, this.layersControl);
    this.lantmäterietSetupService.setupMapBaselayerChangeCallback(map, defaultColor);
  }

  public setupScaleControl(map: L.Map) {
    const options = { metric: true, imperial: false } as L.Control.ScaleOptions;

    L.control.scale(options).addTo(map);
  }

  public setupLegendControl(legendItems: Array<IMapLegendItem>, onlegendItemSelectionChanged: (mapMarkerGroupIdentifierKeys: Array<number>) => void) {
    // Dynamically create the component, set properties and attach to view
    const legendComponentRef = this.resolver.resolveComponentFactory(BiMapLegendComponent).create(this.injector);
    this.appRef.attachView(legendComponentRef.hostView); // for making the component a part of the Angular's life cycle
    legendComponentRef.setInput("legendItems", legendItems);
    legendComponentRef.instance.onSelectionChanged = onlegendItemSelectionChanged;

    // Insert the component before map
    const leafletMapContainer$ = $(".map-container.leaflet-container");
    $((legendComponentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement).insertBefore(leafletMapContainer$);
  }

  /**
   * Programmatically changes the current layer on map, e.i. the layer of the Layers Control.
   */
  public changeLayer(mapLayerId: number, map: L.Map) {
    // First remove current layer (if any) and then add new. The Layers Control will update its checkboxes accordingly
    if (this.currentMapLayer) map.removeLayer(this.currentMapLayer);

    const layerName = Object.keys(this._mapLayers).find(layerName => this._mapLayers[layerName].id === mapLayerId);
    if (layerName) {
      this._mapLayers[layerName].layer.addTo(map);
      this.currentMapLayer = this._mapLayers[layerName].layer;
      this._currentMapLayerId = this._mapLayers[layerName].id;
    }
  }

  /**
   * Helper function for creating a marker for a map. Default color is green.
   */
  public createMarker(latLong: L.LatLng, color: ColorEnum = ColorEnum.GREEN, isCritical = false) {
    return new BiMapMarker(latLong, {
      icon: new L.Icon({
        iconUrl: `/StaticFiles/images/leaflet/marker-icon${!isCritical ? color : "-critical"}.png`,
        shadowUrl: "/StaticFiles/images/leaflet/marker-shadow.png",
        iconAnchor: [15, 40],
        className: "bi-pin-marker"
      })
    });
  }

  public createCircleMarker(latLong: L.LatLng, color: ColorEnum = ColorEnum.GREEN, cssClass = "", isCritical = false) {
    return new BiMapMarker(latLong, {
      icon: new L.DivIcon({
        className: "address-div-icon bg-" + color.toLowerCase() + " " + cssClass + (isCritical ? " critical" : "")
      })
    });
  }

  /**
   * Creates a new marker with an icon comming from a url and with an optional popup depending on if popupcontent is provided.
   * @param popupContent Optional text to used for marker popup. There will be non popup if this is not set
   */
  public createMarkerByIconUrl(latLong: L.LatLng, iconUrl: string, popupContent?: string, popupOptions?: L.PopupOptions) {
    const marker = new BiMapMarker(latLong, {
      icon: new L.Icon({
        iconUrl: iconUrl,
        iconAnchor: [15, 40],
        className: "cursor-pointer bi-leaflet-marker"
      }),
      alt: this.translator.instant("shared.Message")
    });

    const options: L.PopupOptions = popupOptions ?? {};

    if (!isStringNullOrEmpty(popupContent)) {
      marker.bindPopup(
        new L.Popup({
          ...options,
          offset: L.point(10, -32),
          maxHeight: popupOptions?.maxHeight ?? 350,
          // Max width must be <= screen width and <= 800px
          maxWidth: popupOptions?.maxWidth && popupOptions.maxWidth < window.innerWidth ? popupOptions.maxWidth : 800 < window.innerWidth ? 800 : window.innerWidth - 35,
          minWidth: popupOptions?.minWidth && popupOptions.minWidth < window.innerWidth ? popupOptions.minWidth : 800 < window.innerWidth ? 800 : window.innerWidth - 35
        }).setContent(popupContent)
      );
    }
    return marker;
  }

  /**
   * Helper function for creating a marker for a map.
   * This automatically determines the color of the marker based on whether the address has phone numbers or not.
   * (green if yes, red if no and otherwise blue).
   * @param color For explicitly specifying the color of the marker
   */
  public createAddressCircleMarker(latLong: L.LatLng, address: BiMapComponentAddressModel, isCritical = false, color?: ColorEnum) {
    let iconClassName = "address-div-icon ";
    if (!color) color = address.hasPhoneNumber == undefined ? ColorEnum.BLUE : address.hasPhoneNumber ? ColorEnum.GREEN : ColorEnum.RED;
    if (!isCritical) {
      if (color === ColorEnum.RED) iconClassName += "bg-red";
      else if (color === ColorEnum.GREEN) iconClassName += "bg-green prioritized";
      else if (color === ColorEnum.BLUE) iconClassName += "bg-blue";
    } else iconClassName += "critical";

    let markerTextContent = address.number ?? "";
    if (address.meters != null) markerTextContent += "-" + address.meters;
    if (address.letter && address.letter != "0") markerTextContent += address.letter;

    return new BiMapMarker(latLong, {
      title: address.displayValue,
      icon: new L.DivIcon({
        popupAnchor: [0, 0],
        iconAnchor: [15, 40],
        html: markerTextContent.toString(),
        className: iconClassName,
        iconSize: [30, 30]
      })
    });
  }

  public createLeafletGeoDataByGeoAddress(address: BiMapComponentAddressModel, areaClickable = true) {
    const geoMarker = new BiMapArea(JSON.parse(address.geo), {
      style: { color: ColorEnum.GREEN, fillColor: ColorEnum.GREEN, weight: 1, className: !areaClickable ? "cursor-auto" : "cursor-pointer" },
      interactive: areaClickable
    });
    geoMarker.address = address;
    geoMarker.type = "property";
    return geoMarker;
  }

  /**
   * Get color for a map entry address marker by statusTypeId and status "isDelivered" flag.
   * As described in task 10437:
   * We ONLY look at the entry address' KVH
   * RED color: no contact info found on the whole address (e.i. status type ID is 14)
   * YELLOW: Contact info found but nothing has been delivered
   * GREEN: At least 1 message has been delivered
   */
  public getMarkerColor(entryAddressStatusTypeId: number, isMessageDelivered: boolean): ColorEnum {
    if (isMessageDelivered) return ColorEnum.GREEN;
    if (entryAddressStatusTypeId != 14) return ColorEnum.YELLOW;
    return ColorEnum.RED;
  }

  public isMarkerInsidePinMarker(pinMarker: L.Marker, marker: BiMapMarker, pinMarkerRadius: number) {
    return pinMarker.getLatLng().distanceTo(marker.getLatLng()) <= pinMarkerRadius;
  }

  /**
   * Helper for setting up draw locales - localization of the Leaflet Draw control plugin
   */
  private setupDrawLocales() {
    const locale = drawLocales("en");

    //locale.edit

    // Toolbar :Buttons
    locale.draw.toolbar.buttons.polygon = this.translator.instant("leaflet.LeafletDrawPolygon");
    locale.draw.toolbar.buttons.polyline = this.translator.instant("leaflet.LeafletDrawPolyline");
    locale.draw.toolbar.buttons.rectangle = this.translator.instant("leaflet.LeafletDrawRectangle");
    locale.draw.toolbar.buttons.circle = this.translator.instant("leaflet.LeafletDrawCircle");
    locale.draw.toolbar.buttons.marker = this.translator.instant("leaflet.LeafletDrawMarker");
    locale.edit.toolbar.buttons.edit = this.translator.instant("leaflet.LeafletEditToolbarButtonsEdit");
    locale.edit.toolbar.buttons.editDisabled = this.translator.instant("leaflet.LeafletEditToolbarButtonsEditDisabled");
    locale.edit.toolbar.buttons.remove = this.translator.instant("leaflet.LeafletEditToolbarButtonsRemove");
    locale.edit.toolbar.buttons.removeDisabled = this.translator.instant("leaflet.LeafletEditToolbarButtonsRemoveDisabled");
    // :Actions
    locale.draw.toolbar.actions.title = this.translator.instant("leaflet.LeafletDrawCancel");
    locale.draw.toolbar.actions.text = this.translator.instant("shared.Cancel");
    locale.edit.toolbar.actions.save.title = this.translator.instant("shared.SaveChanges");
    locale.edit.toolbar.actions.save.text = this.translator.instant("shared.Save");
    locale.edit.toolbar.actions.cancel.title = this.translator.instant("leaflet.LeafletEditToolbarCancelTitle");
    locale.edit.toolbar.actions.cancel.text = this.translator.instant("shared.Cancel");
    locale.edit.toolbar.actions.clearAll.title = this.translator.instant("leaflet.LeafletEditToolbarclearAllText");
    locale.edit.toolbar.actions.clearAll.text = this.translator.instant("leaflet.LeafletEditToolbarclearAllText");
    // :Generic
    locale.draw.toolbar.finish.title = this.translator.instant("leaflet.LeafletFinishTitle");
    locale.draw.toolbar.finish.text = this.translator.instant("leaflet.LeafletFinishText");
    locale.draw.toolbar.undo.title = this.translator.instant("leaflet.LeafletUndoTitle");
    locale.draw.toolbar.undo.text = this.translator.instant("leaflet.LeafletUndoText");
    // Handlers
    locale.edit.handlers.edit.tooltip.text = this.translator.instant("leaflet.LeafletEditHandlersEditTooltipText");
    locale.edit.handlers.edit.tooltip.subtext = this.translator.instant("leaflet.LeafletEditHandlersEditTooltipSubtext");
    locale.edit.handlers.remove.tooltip.text = this.translator.instant("leaflet.LeafletEditHandlersRemoveTooltiptext");
    // :Marker
    locale.draw.handlers.marker.tooltip.start = this.translator.instant("leaflet.LeafletTooltipMarker");
    // :PolyLine
    locale.draw.handlers.polyline.error = this.translator.instant("leaflet.LeafletTooltipPolylineError");
    // :Polygon
    locale.draw.handlers.polygon.tooltip.start = this.translator.instant("leaflet.LeafletTooltipPolylineStart");
    locale.draw.handlers.polygon.tooltip.cont = this.translator.instant("leaflet.LeafletTooltipPolylineCont");
    locale.draw.handlers.polygon.tooltip.end = this.translator.instant("leaflet.LeafletTooltipPolylineEnd");
    // :Rectangle Tooltip
    locale.draw.handlers.rectangle.tooltip.start = this.translator.instant("leaflet.LeafletTooltipRectangleStart");
    // :Circle Tooltip
    locale.draw.handlers.circle.radius = this.translator.instant("shared.Radius");
    locale.draw.handlers.circle.tooltip.start = this.translator.instant("leaflet.LeafletTooltipCircleStart");
    // :Simple Shape - Rectangle/Circle
    locale.draw.handlers.simpleshape.tooltip.end = this.translator.instant("leaflet.LeafletTooltipSimpleShapeEnd") + "<br />";

    L.drawLocal.draw = locale.draw;
    L.drawLocal.edit = locale.edit;
  }

  /**
   * Fixes the missing "double click to finish drawing"-feature, which still isn't fixed by now.
   * Solution copied from here: https://github.com/Leaflet/Leaflet.draw/issues/665#issuecomment-325919039
   */
  private fixDoubleClickToFinishDrawing() {
    (L.Draw.Polyline.prototype as any)._calculateDistance = function (potentialLatLng, marker) {
      let lastPtDistance;
      if (this._markers.length > 0) {
        const markerPoint = this._map.latLngToContainerPoint(marker.getLatLng());
        const potentialMarker = new L.Marker(potentialLatLng, {
          icon: this.options.icon,
          zIndexOffset: this.options.zIndexOffset * 2
        });

        const potentialMarkerPint = this._map.latLngToContainerPoint(potentialMarker.getLatLng());
        lastPtDistance = markerPoint.distanceTo(potentialMarkerPint);
      } else {
        lastPtDistance = Infinity;
      }
      return lastPtDistance;
    };

    const _endPoint = (L.Draw.Polygon.prototype as any)._endPoint;
    (L.Draw.Polygon.prototype as any)._endPoint = function (clientX, clientY, e) {
      if (this._markers.length > 2) {
        const dblClickPointDistance = this._calculateDistance(e.latlng, this._markers[this._markers.length - 1]);
        if (dblClickPointDistance < 10 && L.Browser.touch) {
          this._enableNewMarkers();
          return;
        }
      }

      _endPoint.call(this, clientX, clientY, e);
    };
  }

  public isLayerPolyline(layer: L.Layer) {
    return !(layer instanceof L.Polygon) && !(layer instanceof L.Rectangle) && layer instanceof L.Polyline;
  }
}
